import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { catchError } from "@_utils/helpers";
import { caseActions } from "@_actions/case.actions";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import { customerActions } from "@_actions/customer.actions";

/**
 * Owns every status-driven effect for MailPopup so they behave
 * consistently and share the single, important multi-popup guard:
 * `if (!open) return`.
 *
 * Without that guard every *closed* MailPopup instance mounted on the page
 * reacts to the global case status (e.g. CASE_SEND_EMAIL === "success") and
 * fires snackbars / getCaseActivities loops / cost dialogs. Centralizing the
 * four effects here guarantees only the open popup responds:
 *
 *   1. CASE_GET_LETTER_COST  -> opens the letter-cost dialog
 *   2. CASE_SEND_EMAIL / CASE_SEND_LETTER -> closes popup + refreshes activities
 *   3. CASE_SAVE_EMAIL_TO_LOG -> closes popup + refreshes activities
 *   4. CASE_PREVIEW_LETTER   -> marks the letter as previewed
 *
 * Callbacks are funneled through a ref so the effects can list only
 * data-driven deps without eslint-disable exhaustive-deps.
 */
export function useEmailSendStatus({
  open,
  caseReducer,
  caseId,
  data,
  autoHideDuration,
  isClosingRef,
  closePopup,
  setEditorReady,
  setLoading,
  setErrorSnackbarTitle,
  setErrorMessage,
  setErrorSnackbar,
  setDidPreviewLetter,
  setHasUnreviewedChanges,
  setTextToContent,
  showSnackbarSuccess,
  handleOpenLetterCostPopup,
  handleCloseLetterCostPopup,
}) {
  const dispatch = useDispatch();

  const callbacksRef = useRef(null);
  callbacksRef.current = {
    closePopup,
    setEditorReady,
    setLoading,
    setErrorSnackbarTitle,
    setErrorMessage,
    setErrorSnackbar,
    setDidPreviewLetter,
    setHasUnreviewedChanges,
    setTextToContent,
    showSnackbarSuccess,
    handleOpenLetterCostPopup,
    handleCloseLetterCostPopup,
  };

  // Handle letter cost retrieval — open cost popup on success, show error on failure
  useEffect(() => {
    if (!open) return;
    const cb = callbacksRef.current;
    if (caseReducer.status.CASE_GET_LETTER_COST === "success") {
      cb.handleOpenLetterCostPopup();
      dispatch(caseActions.clearStateMessage("CASE_GET_LETTER_COST"));
      cb.setLoading(false);
    } else if (catchError(caseReducer.status.CASE_GET_LETTER_COST)) {
      cb.setErrorMessage(caseReducer.message);
      cb.setErrorSnackbar(true);
      dispatch(caseActions.clearStateMessage("CASE_GET_LETTER_COST"));
      cb.setLoading(false);
    }
  }, [
    dispatch,
    open,
    caseReducer.status.CASE_GET_LETTER_COST,
    caseReducer.message,
  ]);

  // Handle email/letter send — close popup and refresh activities on success, show error on failure
  useEffect(() => {
    if (!open) return;
    const cb = callbacksRef.current;
    if (
      caseReducer.status.CASE_SEND_EMAIL === "success" ||
      caseReducer.status.CASE_SEND_LETTER === "success"
    ) {
      const wasEmail = caseReducer.status.CASE_SEND_EMAIL === "success";
      const wasLetter = caseReducer.status.CASE_SEND_LETTER === "success";

      // Clear status synchronously so a popup opened immediately after
      // this one closes won't see the stale "success" and re-trigger.
      dispatch(
        caseActions.clearStateMessage(
          wasEmail ? "CASE_SEND_EMAIL" : "CASE_SEND_LETTER",
        ),
      );

      cb.setLoading(false);
      isClosingRef.current = true;
      cb.setEditorReady(false);
      cb.closePopup();
      cb.showSnackbarSuccess(
        wasEmail ? "Email was sent!" : "Letter was sent!",
        { autoHideDuration },
      );
      if (wasLetter) {
        dispatch(customerActions.getCredits());
        cb.handleCloseLetterCostPopup();
      }
      dispatch(caseActivityActions.getCaseActivities(caseId));
        dispatch(caseActivityActions.restEmailLoaded());
        isClosingRef.current = false;
    } else if (
      catchError(caseReducer.status.CASE_SEND_EMAIL) ||
      catchError(caseReducer.status.CASE_SEND_LETTER)
    ) {
      isClosingRef.current = false;
      cb.setErrorSnackbarTitle("Sending mail failed");
      cb.setErrorMessage(caseReducer.message);
      cb.setErrorSnackbar(true);
      cb.setLoading(false);
      dispatch(
        caseActions.clearStateMessage(
          catchError(caseReducer.status.CASE_SEND_EMAIL)
            ? "CASE_SEND_EMAIL"
            : "CASE_SEND_LETTER",
        ),
      );
    }
  }, [
    dispatch,
    open,
    caseReducer.status.CASE_SEND_EMAIL,
    caseReducer.status.CASE_SEND_LETTER,
    caseReducer.message,
    caseId,
    autoHideDuration,
    isClosingRef,
  ]);

  // Handle save-email-to-log — close popup and refresh activities on success, show error on failure
  useEffect(() => {
    if (!open) return;
    const cb = callbacksRef.current;

    const responseStatus = caseReducer.status.CASE_SAVE_EMAIL_TO_LOG;
    if (responseStatus === "success") {
      dispatch(caseActions.clearStateMessage("CASE_SAVE_EMAIL_TO_LOG"));
      cb.showSnackbarSuccess("Email was saved!", { autoHideDuration });
      cb.setLoading(false);

      setTimeout(() => {
        cb.setEditorReady(false);
        cb.closePopup();
        setTimeout(() => {
          cb.setTextToContent(data.content);
          dispatch(caseActivityActions.getCaseActivities(caseId));
          dispatch(caseActivityActions.restEmailLoaded());
        }, 300);
      }, 400);
    } else if (catchError(responseStatus)) {
      dispatch(caseActions.clearStateMessage("CASE_SAVE_EMAIL_TO_LOG"));
      cb.setErrorMessage(caseReducer.message);
      cb.setErrorSnackbar(true);
      cb.setLoading(false);
    }
  }, [
    dispatch,
    open,
    caseReducer.status.CASE_SAVE_EMAIL_TO_LOG,
    caseReducer.message,
    data.content,
    caseId,
    autoHideDuration,
  ]);

  // Handle letter preview — mark as previewed on success, show error on failure
  useEffect(() => {
    if (!open) return;
    const cb = callbacksRef.current;
    if (caseReducer.status.CASE_PREVIEW_LETTER === "success") {
      cb.showSnackbarSuccess("Letter was generated!", { autoHideDuration });
      cb.setLoading(false);
      cb.setDidPreviewLetter(true);
      cb.setHasUnreviewedChanges(false);
      dispatch(caseActions.clearStateMessage("CASE_PREVIEW_LETTER"));
    }
    if (catchError(caseReducer.status.CASE_PREVIEW_LETTER)) {
      dispatch(caseActions.clearStateMessage("CASE_PREVIEW_LETTER"));
      cb.setErrorSnackbarTitle("Preview failed");
      cb.setErrorMessage(caseReducer.message);
      cb.setErrorSnackbar(true);
      cb.setLoading(false);
    }
  }, [
    dispatch,
    open,
    caseReducer.status.CASE_PREVIEW_LETTER,
    caseReducer.message,
    autoHideDuration,
  ]);
}
